import React from "react";
import {
  Users,
  Clock,
  Activity,
  IndianRupee,
  CreditCard,
  Users2,
  InfoIcon,
  LogIn,
  Timer,
  Clock1,
  Building2,
} from "lucide-react";
import StatCard from "./components/startCard";
import PatientAreaChart from "./components/patientAreaChart";
import DepartmentBarChart from "./components/departmentBarChart";
import RevenueDonutChart from "./components/revenueDonutChart";
import PerformanceRadarChart from "./components/performanceRadarChart";
import AdmissionsComposedChart from "./components/admissionsComposedChart";
import ICUOccupancyRadial from "./components/icuOccupancyRadial";
import useAdminProfile from "../../../store/admin/profile";
import ViewAdminProfile from "../../../components/viewsModals/viewAdminProfileModal";
import { useQuery } from "@tanstack/react-query";
import { getAdminDashboardStats } from "../../../services/admin/dashboardService";
import Loader from "../../../components/loader/loader";
import HeroStat from "./components/heroStat";
import MiniStat from "./components/miniStat";
import SectionCard from "./components/sectionCard";
import ActivityStrip from "./components/activityStrip";

const patientData = [
  { name: "Jan", patients: 400, revenue: 2400 },
  { name: "Feb", patients: 300, revenue: 1398 },
  { name: "Mar", patients: 600, revenue: 9800 },
  { name: "Apr", patients: 800, revenue: 3908 },
  { name: "May", patients: 500, revenue: 4800 },
];

const departmentData = [
  { name: "ICU", occupancy: 85 },
  { name: "Pediatrics", occupancy: 45 },
  { name: "Surgery", occupancy: 90 },
  { name: "ER", occupancy: 70 },
];

const revenueSplit = [
  { name: "Surgery", value: 400000 },
  { name: "ICU", value: 300000 },
  { name: "ER", value: 200000 },
  { name: "Pediatrics", value: 150000 },
];

const performanceData = [
  { subject: "ICU", value: 120 },
  { subject: "ER", value: 98 },
  { subject: "Surgery", value: 150 },
  { subject: "Pediatrics", value: 85 },
];

const AdminDashboard = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["stats"],
    queryFn: async () => {
      const res = await getAdminDashboardStats();

      return res?.data?.data;
    },
  });

  return (
    <div className="min-h-screen bg-slate-50 p-2 md:p-8">
      {isLoading && <Loader />}
      {/* Stats */}
      {data && (
        <div className="space-y-8">
          {/* Top Hero KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-2">
            <HeroStat
              label="Total Patients"
              value={data.totalPatients}
              color="blue"
              icon={<Users2 className="w-16 h-16 opacity-90" />}
            />
            <HeroStat
              label="Total Health Cards"
              value={data.totalHealthCards}
              color="blue"
              icon={<CreditCard className="w-16 h-16 opacity-90" />}
            />
            <HeroStat
              label="Total Employees"
              value={data.totalEmployees}
              color="blue"
              icon={<Users className="w-16 h-16 opacity-90" />}
            />
            {/* Health Cards Section */}
            <SectionCard
              icon={<CreditCard className="w-8 h-8 opacity-90" />}
              title={"Cards\nOverview"}
            >
              <div className="grid grid-cols-1 gap-4">
                <MiniStat label="Active Cards" value={data.activeHealthCards} />
                <MiniStat
                  label="Inactive Cards"
                  value={data.inactiveHealthCards}
                />
              </div>
            </SectionCard>

            {/* Patients Section */}
            <SectionCard
              icon={<InfoIcon className="w-8 h-8 opacity-90" />}
              title={"Patients\nOverview"}
            >
              <div className="grid grid-cols-1 gap-4">
                <MiniStat label="Active Patients" value={data.activePatients} />
                <MiniStat
                  label="Today Registrations"
                  value={data.todayRegistrations.patients}
                />
              </div>
            </SectionCard>

            {/* Employees Section */}
            <SectionCard
              icon={<Users2 className="w-8 h-8 opacity-90" />}
              title={"Employees\nOverview"}
            >
              <div className="grid grid-cols-1  gap-4">
                <MiniStat
                  label="Active Employees"
                  value={data.activeEmployees}
                />
                <MiniStat
                  label="Today Registrations"
                  value={data.todayRegistrations.employees}
                />
              </div>
            </SectionCard>
          </div>

          {/* Activity Strip */}
          <ActivityStrip
            stats={[
              {
                label: "Total Logins",
                value: data.totalLogins,
                icon: <LogIn className="w-8 h-8 opacity-90" />,
              },
              {
                label: "Today Logins",
                value: data.todayLogins,
                icon: <Timer className="w-8 h-8 opacity-90" />,
              },
              {
                label: "Active Sessions",
                value: data.activeSessions,
                spin: true,
                icon: <Clock1 className="w-8 h-8 opacity-90" />,
              },
              {
                label: "Total Branches",
                value: data.totalBranches,
                icon: <Building2 className="w-8 h-8 opacity-90" />,
              },
            ]}
          />
        </div>
      )}

      {/* Charts */}
      <div className="grid mt-6 grid-cols-1 lg:grid-cols-2 gap-8">
        <PatientAreaChart data={patientData} />
        <DepartmentBarChart data={departmentData} />
        <RevenueDonutChart data={revenueSplit} />
        <PerformanceRadarChart data={performanceData} />
        <AdmissionsComposedChart data={patientData} />
        <ICUOccupancyRadial value={65} />
      </div>
    </div>
  );
};

export default AdminDashboard;
